import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { timeAgo } from "@/lib/meta";
import { Send, Megaphone, Bot, Copy, ExternalLink, Loader2, Check, Flame, Trophy } from "lucide-react";

const Card = ({ title, icon: Icon, children, badge }) => (
  <div className="rounded-2xl border border-line bg-surface p-5">
    <div className="mb-4 flex items-center gap-2">
      {Icon && <Icon size={16} className="text-juice" />}
      <h2 className="text-sm font-semibold uppercase tracking-wider text-dust">{title}</h2>
      {badge}
    </div>
    {children}
  </div>
);

const SimBadge = ({ live }) => (
  <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold ${live ? "bg-pulp/15 text-pulp" : "bg-raised text-dust"}`}>
    {live ? "LIVE" : "SIMULATED"}
  </span>
);

export default function Telegram() {
  const [status, setStatus] = useState(null);
  const [msg, setMsg] = useState("");
  const [history, setHistory] = useState([]);
  const [launches, setLaunches] = useState([]);
  const [sel, setSel] = useState("");
  const [bot, setBot] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [busy, setBusy] = useState(null);
  const [copied, setCopied] = useState("");

  const loadTop = () => {
    api.get("/telegram/status").then(({ data }) => setStatus(data)).catch(() => {});
    api.get("/announcements").then(({ data }) => setHistory(data)).catch(() => {});
    api.get("/launches").then(({ data }) => { setLaunches(data); if (data[0] && !sel) setSel(data[0].id); }).catch(() => {});
  };
  useEffect(() => { loadTop(); }, []); // eslint-disable-line
  useEffect(() => { if (sel) { api.get(`/telegram/buybot/${sel}`).then(({ data }) => setBot(data)).catch(() => {}); loadAlerts(); } }, [sel]); // eslint-disable-line

  const loadAlerts = () => { if (sel) api.get(`/telegram/buybot/${sel}/alerts`).then(({ data }) => setAlerts(data.alerts || [])).catch(() => {}); };

  const copy = (text, key) => { navigator.clipboard.writeText(text); setCopied(key); toast.success("Copied"); setTimeout(() => setCopied(""), 1500); };

  const sendAnnounce = async () => {
    if (!msg.trim()) return;
    setBusy("announce");
    try {
      const { data } = await api.post("/announcements/send", { text: msg });
      toast[data.status === "sent" ? "success" : "message"](data.status === "sent" ? "Announced to the channel" : "Saved (simulated — paste a bot token to go live)");
      setMsg(""); loadTop();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const setCfg = (patch) => setBot((b) => ({ ...b, config: { ...b.config, ...patch } }));
  const saveBot = async () => {
    setBusy("savebot");
    try { const { data } = await api.post(`/telegram/buybot/${sel}`, bot.config); setBot((b) => ({ ...b, config: data.config })); toast.success("Buy bot saved"); }
    catch { toast.error("Could not save"); }
    finally { setBusy(null); }
  };
  const preview = async () => {
    setBusy("preview");
    try {
      const { data } = await api.post(`/telegram/buybot/${sel}/preview`);
      toast[data.simulated ? "message" : "success"](data.simulated ? "Preview (simulated)" : "Sent to your group", { description: data.detail || undefined });
      setBot((b) => ({ ...b, previewText: data.preview }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };
  const simulateBuy = async () => {
    setBusy("simbuy");
    try {
      const { data } = await api.post(`/telegram/buybot/${sel}/simulate-buy`);
      toast.success(`Fired a $${data.usd} buy alert`, { description: "Simulated — real buys fire automatically once Helius is connected." });
      loadAlerts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };
  const simulateBind = async () => {
    setBusy("bind");
    try {
      await api.post(`/telegram/buybot/${sel}/simulate-bind`);
      toast.success("Group bound (simulated)");
      const { data } = await api.get(`/telegram/buybot/${sel}`); setBot(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };
  const postLeaderboard = async () => {
    setBusy("lb");
    try {
      const { data } = await api.post(`/telegram/buybot/${sel}/post-leaderboard`);
      toast[data.simulated ? "message" : "success"](data.simulated ? "Leaderboard (simulated)" : "Posted to your group", { description: data.detail || undefined });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const c = bot?.config || {};

  return (
    <div className="mx-auto max-w-3xl px-5 py-8 lg:px-8">
      <div className="mb-1 flex items-center gap-2"><Send size={20} className="text-juice" /><h1 className="text-2xl font-bold tracking-tight">Telegram</h1></div>
      <p className="mb-6 text-sm text-dust">Announce every launch to all Wen Moon followers and give each token a configurable buy bot.</p>

      <div className="space-y-5">
        <Card title="Channel announcements" icon={Megaphone} badge={<SimBadge live={status?.live} />}>
          <p className="mb-3 text-xs text-dust">Posts to the official Wen Moon channel. Every new launch is auto-announced with a tap-to-copy contract address.</p>
          <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} placeholder="gm juicers — big things brewing…" className="bg-raised border-line" data-testid="announce-input" />
          <div className="mt-3 flex justify-end">
            <Button onClick={sendAnnounce} disabled={busy === "announce" || !msg.trim()} className="gap-2 bg-juice font-semibold text-ink hover:bg-juice/90" data-testid="announce-send">
              {busy === "announce" ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Announce
            </Button>
          </div>
          <div className="mt-4 space-y-2" data-testid="announce-history">
            {history.slice(0, 6).map((a) => (
              <div key={a.id} className="flex items-start gap-2 rounded-lg bg-raised/40 p-2.5 text-xs">
                <span className={`mt-0.5 rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase ${a.type === "launch" ? "bg-juice/15 text-juice" : "bg-ai/15 text-ai"}`}>{a.type}</span>
                <span className="flex-1 whitespace-pre-wrap text-dust">{(a.text || "").replace(/<\/?[^>]+>/g, "").slice(0, 140)}</span>
                <span className="text-dust/60">{timeAgo(a.created_at)}</span>
              </div>
            ))}
            {!history.length && <p className="text-xs text-dust">No announcements yet.</p>}
          </div>
        </Card>

        <Card title="Buy bot" icon={Bot} badge={<SimBadge live={status?.live} />}>
          <div className="mb-4 flex flex-wrap gap-2" data-testid="buybot-launch-picker">
            {launches.map((l) => (
              <button key={l.id} onClick={() => setSel(l.id)} className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${sel === l.id ? "border-juice bg-juice/10 text-juice" : "border-line text-dust hover:text-cream"}`} data-testid={`buybot-pick-${l.ticker}`}>${l.ticker}</button>
            ))}
            {!launches.length && <p className="text-xs text-dust">Launch a token first to set up a buy bot.</p>}
          </div>

          {bot && (
            <>
              <div className="rounded-xl border border-line bg-raised/40 p-4">
                <p className="mb-2 text-xs text-dust">Add this bot to your <b className="text-cream">${bot.ticker}</b> Telegram group as an admin. You initiate it — we never join automatically.</p>
                <div className="flex flex-wrap items-center gap-2">
                  <code className="rounded bg-ink px-2 py-1 font-mono text-sm text-juice" data-testid="buybot-handle">@{bot.bot_username}</code>
                  <Button size="sm" variant="outline" onClick={() => copy(`@${bot.bot_username}`, "handle")} className="h-7 gap-1 border-line bg-surface text-xs" data-testid="buybot-copy-handle">
                    {copied === "handle" ? <Check size={12} /> : <Copy size={12} />} Copy
                  </Button>
                  <a href={bot.add_link} target="_blank" rel="noreferrer" data-testid="buybot-add-link">
                    <Button size="sm" className="h-7 gap-1 bg-juice text-xs font-semibold text-ink hover:bg-juice/90"><ExternalLink size={12} /> Add to group</Button>
                  </a>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {bot.bound ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-pulp/40 bg-pulp/10 px-2.5 py-1 text-[11px] font-semibold text-pulp" data-testid="buybot-bound-status">
                      <Check size={12} /> Bound to “{bot.config?.group_title || "your group"}”
                    </span>
                  ) : (
                    <>
                      <span className="font-mono text-[11px] text-dust" data-testid="buybot-bound-status">bind code: <span className="text-cream">{bot.bind_code}</span> · not bound yet</span>
                      <Button size="sm" variant="outline" onClick={simulateBind} disabled={busy === "bind"} className="h-7 gap-1 border-line bg-surface text-xs" data-testid="buybot-simulate-bind">
                        {busy === "bind" ? <Loader2 size={12} className="animate-spin" /> : <Bot size={12} />} Simulate group add
                      </Button>
                    </>
                  )}
                </div>
                <p className="mt-1 text-[10px] text-dust/60">Live, the bot auto-confirms the bind the moment you add it to your group.</p>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex items-center justify-between rounded-lg bg-raised/40 p-3">
                  <span className="text-sm text-cream">Enabled</span>
                  <Switch checked={!!c.enabled} onCheckedChange={(v) => setCfg({ enabled: v })} data-testid="buybot-enabled" />
                </div>
                <div className="space-y-1"><Label className="text-xs text-dust">Min buy to alert ($)</Label>
                  <Input type="number" value={c.min_buy_usd ?? 10} onChange={(e) => setCfg({ min_buy_usd: parseFloat(e.target.value) || 0 })} className="bg-raised border-line font-mono" data-testid="buybot-minbuy" /></div>
                <div className="space-y-1"><Label className="text-xs text-dust">Emoji</Label>
                  <Input value={c.emoji ?? "🟢"} onChange={(e) => setCfg({ emoji: e.target.value })} className="bg-raised border-line" data-testid="buybot-emoji" /></div>
                <div className="space-y-1"><Label className="text-xs text-dust">$ per emoji</Label>
                  <Input type="number" value={c.emoji_step ?? 10} onChange={(e) => setCfg({ emoji_step: parseFloat(e.target.value) || 1 })} className="bg-raised border-line font-mono" data-testid="buybot-step" /></div>
                <div className="space-y-1 sm:col-span-2"><Label className="text-xs text-dust">Media URL (optional gif/image)</Label>
                  <Input value={c.media_url ?? ""} onChange={(e) => setCfg({ media_url: e.target.value })} placeholder="https://…" className="bg-raised border-line font-mono" data-testid="buybot-media" /></div>
                <div className="space-y-1"><Label className="text-xs text-dust">🐋 Whale buy from ($)</Label>
                  <Input type="number" value={c.whale_min_usd ?? 500} onChange={(e) => setCfg({ whale_min_usd: parseFloat(e.target.value) || 0 })} className="bg-raised border-line font-mono" data-testid="buybot-whale" /></div>
                <div className="space-y-1"><Label className="text-xs text-dust">Auto-post leaderboard (hrs, 0=off)</Label>
                  <Input type="number" value={c.leaderboard_hours ?? 0} onChange={(e) => setCfg({ leaderboard_hours: parseFloat(e.target.value) || 0 })} className="bg-raised border-line font-mono" data-testid="buybot-lbhours" /></div>
              </div>

              {bot.previewText && (
                <pre className="mt-4 whitespace-pre-wrap rounded-lg border border-line bg-ink p-3 text-xs text-cream" data-testid="buybot-preview">{bot.previewText.replace(/<\/?[^>]+>/g, "")}</pre>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                <Button onClick={saveBot} disabled={busy === "savebot"} className="gap-2 bg-juice font-semibold text-ink hover:bg-juice/90" data-testid="buybot-save">
                  {busy === "savebot" ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save
                </Button>
                <Button onClick={preview} disabled={busy === "preview"} variant="outline" className="gap-2 border-line bg-surface" data-testid="buybot-preview-btn">
                  {busy === "preview" ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send preview
                </Button>
                <Button onClick={simulateBuy} disabled={busy === "simbuy"} variant="outline" className="gap-2 border-pulp/40 bg-pulp/10 text-pulp hover:bg-pulp/20" data-testid="buybot-simulate-btn">
                  {busy === "simbuy" ? <Loader2 size={15} className="animate-spin" /> : <Flame size={15} />} Simulate a buy
                </Button>
                <Button onClick={postLeaderboard} disabled={busy === "lb"} variant="outline" className="gap-2 border-line bg-surface" data-testid="buybot-leaderboard-btn">
                  {busy === "lb" ? <Loader2 size={15} className="animate-spin" /> : <Trophy size={15} />} Post leaderboard
                </Button>
              </div>

              <div className="mt-5 rounded-xl border border-line bg-ink/40 p-4" data-testid="buybot-alerts">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wider text-dust">Recent buys</p>
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${bot.live && status?.buy_detection ? "bg-pulp/15 text-pulp" : "bg-raised text-dust"}`} data-testid="buybot-detection-status">
                    <span className={`h-1.5 w-1.5 rounded-full ${status?.buy_detection ? "bg-pulp animate-soft-pulse" : "bg-dust/50"}`} />
                    {status?.buy_detection ? "Live detection on" : "Detection off (connect Helius)"}
                  </span>
                </div>
                {alerts.length ? (
                  <div className="max-h-52 space-y-1.5 overflow-y-auto">
                    {alerts.map((a) => (
                      <div key={a.id} className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm ${a.whale ? "border border-juice/50 bg-juice/10 py-3" : "bg-raised/40"}`} data-testid={`buybot-alert-${a.id}`}>
                        <span className="flex items-center gap-2">
                          {a.whale ? <span className="text-base">🐋</span> : <span className="h-1.5 w-1.5 rounded-full bg-pulp" />}
                          <span className={`font-medium ${a.whale ? "font-bold text-juice" : "text-cream"}`}>{a.whale ? `WHALE $${a.ticker}` : `$${a.ticker} buy`}</span>
                          {a.whale && <span className="rounded bg-juice/20 px-1.5 py-0.5 text-[9px] font-bold uppercase text-juice" data-testid={`buybot-whale-${a.id}`}>whale</span>}
                          {a.simulated && <span className="rounded bg-ink px-1.5 py-0.5 text-[9px] uppercase text-dust">sim</span>}
                        </span>
                        <span className="flex items-center gap-3"><span className={`font-mono ${a.whale ? "text-base font-bold text-juice" : "text-pulp"}`}>${a.usd}</span><span className="text-[11px] text-dust">{timeAgo(a.created_at)}</span></span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs text-dust">No buys yet. Hit "Simulate a buy" to preview the alert, or connect Helius for live detection.</p>}
              </div>
            </>
          )}
        </Card>

        <p className="text-xs text-dust/70">Live once an admin pastes a Telegram bot token (from @BotFather). Real on-chain buy detection switches on automatically when <span className="font-mono">SOLANA_RPC_URL</span> (Helius) is set — until then, use "Simulate a buy" to preview.</p>
      </div>
    </div>
  );
}
